import { useEffect, useMemo, useState } from 'react'
import { useSubmit } from '../../../hooks/useSubmit'
import { useRequestId } from '../../../hooks/useRequestId'
import { useUnsavedWork } from '../../../contexts/UnsavedWorkContext'
import { addStockReceipt } from '../../adminApi'
import { today } from '../../../core/format/dates'
import { toCents as centsOf } from '../../../core/format/currency'
import { receiptFileProblem, receiptMismatchCents, receiptProblems, receiptTotalCents } from '../../../core/rules/stockCount'
import type { PurchaseLineInput, PurchaseReceipt, PurchaseUse, StockItem, StockLocation } from '../../../core/types/stock'

/** Een regel zoals Sarah hem invult: prijzen in euro's, leeg mag tijdens het typen. */
export type ReceiptDraftLine = { key: string; itemId: string; qty: number | undefined; cost: number | undefined; use: PurchaseUse }

let seq = 0
const newLine = (): ReceiptDraftLine => ({ key: `inkoopregel-${++seq}`, itemId: '', qty: undefined, cost: undefined, use: 'winkel' })
// Via de gedeelde omrekening: `1.005 * 100` is 100.4999…, die schuift de komma zonder afrondingsfout.
const toCents = (euro: number | undefined) => (euro === undefined ? undefined : centsOf(euro))

/**
 * Het formulier "Inkoopfactuur boeken": de bijlage, de kop van de factuur en de regels. Rekent met
 * dezelfde regels als de server (core/rules/stockCount.ts); pas na het antwoord is het geboekt.
 */
export function useReceiptForm(items: readonly StockItem[]) {
  const [file, setFileState] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [supplier, setSupplier] = useState('')
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(today())
  const [location, setLocation] = useState<StockLocation>('webshop')
  const [invoiceTotal, setInvoiceTotal] = useState<number | undefined>(undefined)
  const [invoiceVat, setInvoiceVat] = useState<number | undefined>(undefined)
  const [note, setNote] = useState('')
  const [lines, setLines] = useState<ReceiptDraftLine[]>(() => [newLine()])
  const [touched, setTouched] = useState(false)
  const { submit, submitting, error, fieldErrors } = useSubmit(addStockReceipt)

  // Voorvertoning van een foto; een PDF krijgt een pictogram met de naam.
  const preview = useMemo(() => (file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null), [file])
  useEffect(() => () => (preview ? URL.revokeObjectURL(preview) : undefined), [preview])

  const setFile = (next: File | null) => {
    if (!next) {
      setFileState(null)
      setFileError(null)
      return
    }
    const problem = receiptFileProblem(next)
    setFileError(problem)
    setFileState(problem ? null : next)
  }

  const setLine = (key: string, patch: Partial<ReceiptDraftLine>) =>
    setLines((all) =>
      all.map((l) => {
        if (l.key !== key) return l
        const next = { ...l, ...patch }
        // Een sample is gratis: de prijs wordt € 0 (zo klopt het factuurtotaal ook).
        if (patch.use === 'sample') next.cost = 0
        // Artikel gekozen en nog geen prijs: de bekende inkoopprijs alvast invullen.
        if (patch.itemId && l.cost === undefined) {
          const known = items.find((i) => i.id === patch.itemId)?.costPriceCents
          if (known !== undefined) next.cost = known / 100
        }
        return next
      }),
    )
  const addLine = () => setLines((all) => [...all, newLine()])
  const removeLine = (key: string) => setLines((all) => (all.length === 1 ? [newLine()] : all.filter((l) => l.key !== key)))

  const inputs: PurchaseLineInput[] = lines.map((l) => {
    const costPriceCents = toCents(l.cost)
    return { itemId: l.itemId, qty: l.qty ?? 0, ...(costPriceCents !== undefined ? { costPriceCents } : {}), ...(l.use !== 'winkel' ? { use: l.use } : {}) }
  })
  const problems = receiptProblems({ supplier, invoiceNumber, invoiceDate, lines: inputs }, today())
  const totalCents = receiptTotalCents(inputs)
  const pieces = inputs.reduce((s, l) => s + l.qty, 0)
  const mismatch = receiptMismatchCents(inputs, toCents(invoiceTotal))
  const dirty = Boolean(file || supplier.trim() || invoiceNumber.trim() || lines.some((l) => l.itemId || l.qty))
  useUnsavedWork(dirty, 'page')

  const payload = {
    supplier: supplier.trim(),
    invoiceNumber: invoiceNumber.trim(),
    invoiceDate,
    location,
    lines: inputs,
    ...(invoiceTotal !== undefined ? { invoiceTotalCents: toCents(invoiceTotal) } : {}),
    ...(invoiceVat !== undefined ? { invoiceVatCents: toCents(invoiceVat) } : {}),
    ...(note.trim() ? { note: note.trim() } : {}),
  }
  // Eén requestId per inhoud: een herhaalde klik boekt nooit dubbel, een aangepaste factuur is een nieuw verzoek.
  const attempt = JSON.stringify({ ...payload, file: file ? [file.name, file.size, file.lastModified] : null })
  const requestId = useRequestId(attempt)

  const save = async (): Promise<PurchaseReceipt | null> => {
    setTouched(true)
    if (Object.keys(problems).length > 0 || fileError) return null
    return submit({ requestId, ...payload, ...(file ? { file } : {}) })
  }

  const show = (key: string) => (touched ? (problems[key] ?? fieldErrors[key]) : fieldErrors[key])

  return {
    file,
    setFile,
    fileError,
    preview,
    supplier,
    setSupplier,
    invoiceNumber,
    setInvoiceNumber,
    invoiceDate,
    setInvoiceDate,
    location,
    setLocation,
    invoiceTotal,
    setInvoiceTotal,
    invoiceVat,
    setInvoiceVat,
    note,
    setNote,
    lines,
    setLine,
    addLine,
    removeLine,
    totalCents,
    pieces,
    mismatch,
    show,
    save,
    submitting,
    error,
  }
}

export type ReceiptFormControl = ReturnType<typeof useReceiptForm>
